import { useMemo, useRef, useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
  FloatingPortal,
} from "@floating-ui/react";
import {
  Building2,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  FileText,
  ArrowUpRight,
  Globe,
  Linkedin,
  Star,
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import Flag from "react-world-flags";
import lookup from "country-code-lookup";
import { FilterSheet } from "./filter-sheet";
import {
  useDealRadar,
  useInfiniteDealRadar,
  useCreateOpportunityApplication,
  Company,
} from "@/hooks/use-deal-radar";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "../ui/skeleton";
import { getStageText } from "@/lib/utils";
import { CompanyCard } from "./company-card";

const stageLabelFromValue = (stage?: number | string | null) => {
  if (stage === undefined || stage === null || stage === "" || stage === 0) return "–";
  return getStageText(stage);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return "–";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "–";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

interface FilterValues {
  countries: string[];
  industries: string[];
  sources: string[];
}

export default function DealRadarComponent() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState<FilterValues>({
    countries: [],
    industries: [],
    sources: [],
  });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(
    new Set()
  );
  const [isBatchAdding, setIsBatchAdding] = useState(false);

  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [referenceEl, setReferenceEl] = useState<HTMLElement | null>(null);
  const hoverTimeout = useRef<number | null>(null);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: !!activeCompanyId,
    placement: "right",
    middleware: [
      offset(16),
      flip({ fallbackPlacements: ["left", "top", "bottom"] }),
      shift({ padding: 12 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
    elements: {
      reference: referenceEl,
    },
  });

  const isDesktop = useMediaQuery("(min-width: 768px)");

  // Desktop Query (Paginated)
  const { data: paginatedData, isLoading: isPaginatedLoading } = useDealRadar(
    page,
    10,
    activeFilters,
    activeSearchQuery,
    { enabled: isDesktop } // Only fetch if desktop
  );

  // Mobile Query (Infinite)
  const {
    data: infiniteData,
    isLoading: isInfiniteLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteDealRadar(10, activeFilters, activeSearchQuery, {
    enabled: !isDesktop, // Only fetch if mobile
  });

  const { ref: loadingRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isDesktop) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isDesktop]);

  // Derived state for companies based on view
  const companies = useMemo(() => {
    if (isDesktop) {
      return paginatedData?.companies || [];
    }
    return infiniteData?.pages.flatMap((page) => page.companies) || [];
  }, [isDesktop, paginatedData?.companies, infiniteData?.pages]);

  const isLoading = isDesktop ? isPaginatedLoading : isInfiniteLoading;

  const createOpportunityApplication = useCreateOpportunityApplication();
  const queryClient = useQueryClient();

  const handleAddToPipeline = async (companyId: string) => {
    try {
      await createOpportunityApplication.mutateAsync(companyId);
      toast.success("Company added to pipeline");
      queryClient.invalidateQueries({ queryKey: ["deal-radar"] });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add company";
      toast.error(message);
    }
  };

  /* Progress Dialog State */
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);

  const handleBatchAddToPipeline = async () => {
    if (selectedCompanyIds.size === 0) return;

    // Initialize Progress
    setIsBatchAdding(true);
    setIsProgressDialogOpen(true);
    setProgressTotal(selectedCompanyIds.size);
    setProgressCurrent(0);

    let successCount = 0;
    let failCount = 0;

    // Convert Set to Array to iterate
    const ids = Array.from(selectedCompanyIds);

    for (const id of ids) {
      try {
        await createOpportunityApplication.mutateAsync(id);
        successCount++;
        // Update progress bar
        setProgressCurrent((prev) => prev + 1);
      } catch (error) {
        console.error(`Failed to add company ${id}:`, error);
        failCount++;
        // Update progress bar even on failure to keep it moving
        setProgressCurrent((prev) => prev + 1);
      }
    }

    // Completion Handlers
    setIsProgressDialogOpen(false);
    setIsBatchAdding(false);

    // Clear selection now that we are done
    setSelectedCompanyIds(new Set());

    if (successCount > 0) {
      toast.success(`Successfully added ${successCount} companies to pipeline`);
      queryClient.invalidateQueries({ queryKey: ["deal-radar"] });
    }

    if (failCount > 0) {
      toast.error(`Failed to add ${failCount} companies`);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all visible companies
      const visibleIds = companies.map((c) => c._id);
      setSelectedCompanyIds((prev) => {
        const newSet = new Set(prev);
        visibleIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    } else {
      // Deselect all visible companies
      const visibleIds = companies.map((c) => c._id);
      setSelectedCompanyIds((prev) => {
        const newSet = new Set(prev);
        visibleIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    }
  };

  const handleSelectRow = (companyId: string, checked: boolean) => {
    setSelectedCompanyIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(companyId);
      } else {
        newSet.delete(companyId);
      }
      return newSet;
    });
  };

  const areAllVisibleSelected =
    companies.length > 0 &&
    companies.every((c) => selectedCompanyIds.has(c._id));

  const isIndeterminate =
    companies.some((c) => selectedCompanyIds.has(c._id)) &&
    !areAllVisibleSelected;

  const handleSearch = () => {
    // Reset page when searching
    setPage(1);
    setActiveSearchQuery(searchQuery);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // companies useMemo removed as it is now defined above

  const getAvatarUrl = (company: Company) => {
    const avatar = company.company_file_ids?.find(
      (f) => f.fileType === "company avatar"
    );
    return avatar?.fileId;
  };

  const getPitchDeckUrl = (company: Company) => {
    const deck = company.company_file_ids?.find(
      (f) => f.fileType === "company pitchdeck"
    );
    return deck?.fileId;
  };

  const getPitchDeckViewerUrl = (url?: string) => {
    if (!url) return null;
    try {
      return `https://docs.google.com/viewer?url=${encodeURIComponent(
        url
      )}&embedded=true`;
    } catch {
      return url;
    }
  };

  const handleHover = (id: string, anchorEl: HTMLElement | null) => {
    if (hoverTimeout.current) {
      window.clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setActiveCompanyId(id);
    if (anchorEl) {
      setReferenceEl(anchorEl);
    }
  };

  const handleLeave = () => {
    hoverTimeout.current = window.setTimeout(() => {
      setActiveCompanyId(null);
    }, 120);
  };

  return (
    <div className="flex flex-col h-full overflow-auto px-2 sm:px-4">
      {/* Header - sticky to the scroll container; top padding lives here (not on
          the scroll container) so the opaque background covers content scrolling
          underneath instead of leaving a transparent gap above the search bar. */}
      <div className="sticky top-0 z-10 bg-background pt-3 pb-4 md:pt-6 flex flex-row items-center justify-between gap-3 md:gap-4 mb-2 md:mb-6">
        <div className="flex items-center gap-4 flex-1 min-w-0 md:flex-none md:w-auto">
          <div className="flex items-center gap-2 w-full md:w-72">
            <div className="relative flex-1">
              <Input
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pr-12"
              />
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={handleSearch}
              >
                <Search className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-4 shrink-0">
          {selectedCompanyIds.size > 0 && (
            <Button
              size="sm"
              onClick={handleBatchAddToPipeline}
              disabled={isBatchAdding}
            >
              {isBatchAdding ? (
                <>Adding...</>
              ) : (
                <>Add {selectedCompanyIds.size} to Pipeline</>
              )}
            </Button>
          )}
          <FilterSheet onFiltersChange={setActiveFilters} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        {/* Mobile View - Cards (Infinite Scroll) */}
        <div className="md:hidden flex flex-col gap-4 pb-4">
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-card rounded-xl border p-4 space-y-4"
                >
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-12 w-12 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                </div>
              ))
            : companies.map((company, index) => (
                <CompanyCard
                  key={company._id}
                  company={company}
                  rank={index + 1}
                  onAddToPipeline={handleAddToPipeline}
                  isAddingToPipeline={createOpportunityApplication.isPending}
                  isExpanded={expandedCardId === company._id}
                  onToggle={() => setExpandedCardId(prev => prev === company._id ? null : company._id)}
                />
              ))}

          {/* Infinite Scroll Sentinel */}
          {!isDesktop && (
            <div ref={loadingRef} className="py-4 flex justify-center">
              {isFetchingNextPage && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Loading more...
                </div>
              )}
            </div>
          )}
        </div>

        {/* Desktop View - Table */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={
                      areAllVisibleSelected
                        ? true
                        : isIndeterminate
                        ? "indeterminate"
                        : false
                    }
                    onCheckedChange={(checked) =>
                      handleSelectAll(checked as boolean)
                    }
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Industries</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-4 w-4" />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-[200px]" />
                            <Skeleton className="h-3 w-[150px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Skeleton className="h-4 w-[80px]" />
                          <Skeleton className="h-4 w-[80px]" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[100px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[120px]" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-8 w-8 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                : companies.map((company) => {
                    const avatarUrl = getAvatarUrl(company);
                    const pitchDeckUrl = getPitchDeckUrl(company);
                    const pitchDeckViewerUrl =
                      getPitchDeckViewerUrl(pitchDeckUrl);
                    const linkedInUrl = company.linkedIn;
                    const websiteUrl = company.company_website;
                    const stageLabel = stageLabelFromValue(company.stage);
                    const addedDate = formatDate(company.created_on);
                    const isActive = activeCompanyId === company._id;
                    const isSelected = selectedCompanyIds.has(company._id);

                    const isHighlighted = company.deal_radar_highlight;

                    return (
                      <TableRow
                        key={company._id}
                        className={`
                          ${isHighlighted ? "bg-amber-50/50 dark:bg-amber-950/20 border-l-4 border-l-amber-400" : ""}
                          ${isActive && !isHighlighted ? "border-l-4 border-l-blue-500 bg-muted/40" : ""}
                          ${isSelected && !isHighlighted && !isActive ? "bg-muted/20" : ""}
                        `.trim()}
                        onMouseLeave={handleLeave}
                        onClick={() => setActiveCompanyId(company._id)}
                        data-state={isSelected ? "selected" : undefined}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) =>
                              handleSelectRow(company._id, checked as boolean)
                            }
                            aria-label={`Select ${company.company_name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={`${company.company_name} logo`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <Building2 className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div
                              className="min-w-0 relative"
                              onMouseEnter={(e) =>
                                handleHover(company._id, e.currentTarget)
                              }
                            >
                              <div className="font-medium truncate flex items-center gap-2 max-w-[190px] hover:underline cursor-pointer">
                                {isHighlighted && (
                                  <Star className="h-4 w-4 text-amber-500 fill-amber-400 flex-shrink-0" />
                                )}
                                {company.company_name}
                                {company.company_file_ids &&
                                  company.company_file_ids.length > 0 && (
                                    <FileText className="h-4 w-4 text-blue-500" />
                                  )}
                              </div>
                              <div className="text-sm text-muted-foreground line-clamp-2">
                                {company.short_bio || "No description"}
                              </div>

                              {isActive && (
                                <FloatingPortal>
                                  <div
                                    ref={refs.setFloating}
                                    style={floatingStyles}
                                    className="z-50"
                                    onMouseEnter={() =>
                                      handleHover(company._id, null)
                                    }
                                    onMouseLeave={handleLeave}
                                  >
                                    <FloatingArrow
                                      ref={arrowRef}
                                      context={context}
                                      className="fill-card [&>path:first-of-type]:stroke-blue-500"
                                      width={16}
                                      height={8}
                                    />
                                    <div className="w-[360px] rounded-xl border border-blue-500 bg-card shadow-2xl p-4">
                                      <div className="flex gap-3 mb-3">
                                        <div className="shrink-0 h-14 w-14 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                                          {avatarUrl ? (
                                            <img
                                              src={avatarUrl}
                                              alt={`${company.company_name} logo`}
                                              className="h-full w-full object-cover"
                                            />
                                          ) : (
                                            <Building2 className="h-7 w-7 text-muted-foreground" />
                                          )}
                                        </div>
                                        <div className="min-w-0">
                                          <div className="font-semibold text-lg truncate">
                                            {company.company_name}
                                          </div>
                                          <div className="text-sm text-muted-foreground line-clamp-3">
                                            {company.short_bio ||
                                              "No description available."}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex flex-col gap-3 text-sm text-muted-foreground border-t pt-3">
                                        <div>
                                          <div className="text-xs uppercase tracking-wide text-muted-foreground/80">
                                            Country
                                          </div>
                                          <div className="text-foreground flex items-center gap-2 mt-1">
                                            {company.country ? (
                                              <>
                                                {lookup.byCountry(
                                                  company.country
                                                )?.iso2 && (
                                                  <div className="w-5 flex-shrink-0">
                                                    <Flag
                                                      code={
                                                        lookup.byCountry(
                                                          company.country
                                                        )?.iso2
                                                      }
                                                      className="w-full h-auto shadow-sm rounded-sm"
                                                      style={{
                                                        display: "block",
                                                      }}
                                                    />
                                                  </div>
                                                )}
                                                <span>{company.country}</span>
                                              </>
                                            ) : (
                                              "–"
                                            )}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-xs uppercase tracking-wide text-muted-foreground/80">
                                            Industries
                                          </div>
                                          <div className="text-foreground mt-1 flex flex-wrap gap-1">
                                            {(company.industry || [])
                                              .slice(0, 3)
                                              .map((industry) => (
                                                <Badge
                                                  key={industry}
                                                  variant="secondary"
                                                >
                                                  {industry}
                                                </Badge>
                                              ))}
                                            {company.industry &&
                                              company.industry.length > 3 && (
                                                <Badge variant="outline">
                                                  +{company.industry.length - 3}
                                                </Badge>
                                              )}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="mt-4 flex flex-wrap gap-2">
                                        {websiteUrl && (
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            asChild
                                          >
                                            <a
                                              href={websiteUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              <Globe className="h-4 w-4 mr-2" />{" "}
                                              Website
                                            </a>
                                          </Button>
                                        )}
                                        {linkedInUrl && (
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            asChild
                                          >
                                            <a
                                              href={linkedInUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              <Linkedin className="h-4 w-4 mr-2" />{" "}
                                              LinkedIn
                                            </a>
                                          </Button>
                                        )}
                                        {pitchDeckViewerUrl && (
                                          <Button
                                            size="sm"
                                            className="gap-2"
                                            asChild
                                          >
                                            <a
                                              href={pitchDeckViewerUrl}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              <ArrowUpRight className="h-4 w-4" />{" "}
                                              View Pitch Deck
                                            </a>
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </FloatingPortal>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {company.city || company.province ? (
                              <>
                                {company.city && (
                                  <span className="truncate max-w-[140px]">
                                    {company.city}
                                  </span>
                                )}
                                {company.city && company.province && ","}
                                {company.province && (
                                  <span>{company.province}</span>
                                )}
                              </>
                            ) : (
                              company.country || "–"
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(company.industry || [])
                              .slice(0, 3)
                              .map((industry) => (
                                <Badge key={industry} variant="secondary">
                                  {industry}
                                </Badge>
                              ))}
                            {company.industry &&
                              company.industry.length > 3 && (
                                <Badge variant="outline">
                                  +{company.industry.length - 3}
                                </Badge>
                              )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-medium">
                            {stageLabel}
                          </Badge>
                        </TableCell>
                        <TableCell>{addedDate}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={
                                  createOpportunityApplication.isPending
                                }
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() =>
                                  handleAddToPipeline(company._id)
                                }
                              >
                                Add to Pipeline
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination - Desktop Only */}
      {isDesktop &&
        paginatedData?.pagination &&
        paginatedData?.companies.length > 0 && (
          <div className="mt-4 flex items-center justify-between px-2">
            <div className="text-sm text-muted-foreground">
              Showing {(page - 1) * 10 + 1} to{" "}
              {Math.min(page * 10, paginatedData.pagination.total)} of{" "}
              {paginatedData.pagination.total} companies
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm">
                Page {page} of {paginatedData.pagination.pages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page === paginatedData.pagination.pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      {/* Progress Dialog */}
      <Dialog open={isProgressDialogOpen} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Adding to Pipeline</DialogTitle>
            <DialogDescription>
              Please wait while we add the selected companies to your pipeline.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Progress
              value={
                progressTotal > 0 ? (progressCurrent / progressTotal) * 100 : 0
              }
              className="w-full"
            />
            <p className="text-sm text-center text-muted-foreground">
              Processing {progressCurrent} of {progressTotal} companies...
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
